import {
  Method,
  RequestBodyType,
  ResponseBodyType,
  type RequestConfig,
  prepare,
} from '@bigo/yapi-to-ts';
import request from './request';
import makeRequestHook from './makeRequestHook';

function makeRequest<TRequestData, TResponseData>(requestConfig: RequestConfig) {
  const req = function requestFn(requestData?: TRequestData, options?: Record<string, any>) {
    return request<TResponseData>(prepare(Object.assign(requestConfig, options), requestData));
  };
  req.requestConfig = requestConfig;
  return req;
}

const mockUrl = 'http://yapi.ppx520.com/mock/3957' as any;
const dataKey = 'data' as any;

export interface AgentOssQryOnlineLiveAnchorsRequest {
  /**
   * 主播 BIGO ID，可选；传入时仅在当前权限范围内精确搜索该主播。
   */
  anchor_bigoid?: string;
  /**
   * 经纪人 uid，可选；代理 / 子代理视角传入时筛选该经纪人管理范围，经纪人视角忽略该字段。
   */
  broker_uid?: number;
  /**
   * 页码，从 1 开始；为空或小于 1 时服务端按 1 处理。
   */
  page: number;
  /**
   * 每页条数；服务端会按配置上限裁剪，避免一次探测过多在线状态。
   */
  page_size: number;
}

export interface AgentOssQryOnlineLiveAnchorsResponse {
  /**
   * 当前权限范围内正在直播且可返回给前端选择的主播总数。
   */
  total_cnt: number;
  /**
   * 当前页码，从 1 开始。
   */
  page: number;
  /**
   * 当前每页条数，已经过服务端上限裁剪。
   */
  page_size: number;
  /**
   * 当前页在线主播列表。
   */
  anchor_list: {
    /**
     * 主播 uid。
     */
    uid: number;
    /**
     * 主播 BIGO ID，用户资料缺失时可能为空。
     */
    bigo_id: string;
    /**
     * 主播昵称，用户资料缺失时可能为空。
     */
    nickname: string;
    /**
     * 主播头像 URL，用户资料缺失时可能为空。
     */
    avatar: string;
    /**
     * 当前直播房间 id。
     */
    room_id: string;
    /**
     * 本次开播时间，Unix 秒级时间戳；前端据此计算开播时长。
     */
    live_start_time: number;
  }[];
}

type AgentOssQryOnlineLiveAnchorsRequestConfig = Readonly<
  RequestConfig<
    'http://yapi.ppx520.com/mock/3957',
    '/bigo_act_livedata_api/agentOss/qryOnlineLiveAnchors',
    'data',
    string,
    string,
    false
  >
>;

const agentOssQryOnlineLiveAnchorsRequestConfig = {
  mockUrl,
  path: '/bigo_act_livedata_api/agentOss/qryOnlineLiveAnchors',
  isYapi: true,
  isC: false,
  method: Method.POST,
  requestHeaders: {},
  requestBodyType: RequestBodyType.json,
  responseBodyType: ResponseBodyType.json,
  dataKey,
  paramNames: [],
  queryNames: [],
  requestDataOptional: false,
  requestDataJsonSchema: {},
  responseDataJsonSchema: {},
  requestFunctionName: 'agentOssQryOnlineLiveAnchors',
};

export const agentOssQryOnlineLiveAnchors = makeRequest<
  AgentOssQryOnlineLiveAnchorsRequest,
  AgentOssQryOnlineLiveAnchorsResponse
>(agentOssQryOnlineLiveAnchorsRequestConfig);
export const useAgentOssQryOnlineLiveAnchors = makeRequestHook<
  AgentOssQryOnlineLiveAnchorsRequest,
  AgentOssQryOnlineLiveAnchorsRequestConfig,
  ReturnType<typeof agentOssQryOnlineLiveAnchors>
>(agentOssQryOnlineLiveAnchors);

export type AgentOssQryOnlineLiveAnchorCountRequest = Record<string, never>;

export interface AgentOssQryOnlineLiveAnchorCountResponse {
  /**
   * 当前权限范围内正在直播且可添加到实时画面的主播数量，不包含密码房。
   */
  live_cnt: number;
}

type AgentOssQryOnlineLiveAnchorCountRequestConfig = Readonly<
  RequestConfig<
    'http://yapi.ppx520.com/mock/3957',
    '/bigo_act_livedata_api/agentOss/qryOnlineLiveAnchorCount',
    'data',
    string,
    string,
    true
  >
>;

const agentOssQryOnlineLiveAnchorCountRequestConfig = {
  mockUrl,
  path: '/bigo_act_livedata_api/agentOss/qryOnlineLiveAnchorCount',
  isYapi: true,
  isC: false,
  method: Method.POST,
  requestHeaders: {},
  requestBodyType: RequestBodyType.json,
  responseBodyType: ResponseBodyType.json,
  dataKey,
  paramNames: [],
  queryNames: [],
  requestDataOptional: true,
  requestDataJsonSchema: {},
  responseDataJsonSchema: {},
  requestFunctionName: 'agentOssQryOnlineLiveAnchorCount',
};

export const agentOssQryOnlineLiveAnchorCount = makeRequest<
  AgentOssQryOnlineLiveAnchorCountRequest,
  AgentOssQryOnlineLiveAnchorCountResponse
>(agentOssQryOnlineLiveAnchorCountRequestConfig);
export const useAgentOssQryOnlineLiveAnchorCount = makeRequestHook<
  AgentOssQryOnlineLiveAnchorCountRequest,
  AgentOssQryOnlineLiveAnchorCountRequestConfig,
  ReturnType<typeof agentOssQryOnlineLiveAnchorCount>
>(agentOssQryOnlineLiveAnchorCount);

export interface AgentOssQryLiveStreamRequest {
  /**
   * 主播 uid，必须属于当前登录账号的实时画面范围。
   */
  uid: number;
  /**
   * 密码房验证密码。本需求后端会过滤密码房，前端固定传空。
   */
  verify: string;
  /**
   * 拉流来源。当前实时画面按后端默认识别逻辑处理，前端固定传空。
   */
  source: string;
  /**
   * 主播 BIGO ID，作为官网 Studio 接口的 siteId。
   */
  bigo_id: string;
  /**
   * 密码是否使用 MD5 加密。本需求后端会过滤密码房，前端固定传 0。
   */
  is_encrypt: string;
  /**
   * 是否支持 HEVC，0 不支持，1 支持。
   */
  support_hevc: string;
  /**
   * BigoCaptcha 生成的游客风控 token，用于后端代请求官网取流接口。
   */
  token: string;
}

export interface AgentOssQryLiveStreamResponse {
  /**
   * 主播是否在播，沿用原官网取流接口返回口径。
   */
  alive: number | string;
  /**
   * HLS 播放流地址。
   */
  hls_src: string;
  /**
   * 当前直播房间 id。
   */
  roomId: string;
  /**
   * 主播 uid。
   */
  uid: number | string;
  [key: string]: any;
}

type AgentOssQryLiveStreamRequestConfig = Readonly<
  RequestConfig<
    'http://yapi.ppx520.com/mock/3957',
    '/bigo_act_livedata_api/agentOss/qryLiveStream',
    'data',
    string,
    string,
    false
  >
>;

const agentOssQryLiveStreamRequestConfig = {
  mockUrl,
  path: '/bigo_act_livedata_api/agentOss/qryLiveStream',
  isYapi: true,
  isC: false,
  method: Method.POST,
  requestHeaders: {},
  requestBodyType: RequestBodyType.json,
  responseBodyType: ResponseBodyType.json,
  dataKey,
  paramNames: [],
  queryNames: [],
  requestDataOptional: false,
  requestDataJsonSchema: {},
  responseDataJsonSchema: {},
  requestFunctionName: 'agentOssQryLiveStream',
};

export const agentOssQryLiveStream = makeRequest<
  AgentOssQryLiveStreamRequest,
  AgentOssQryLiveStreamResponse
>(agentOssQryLiveStreamRequestConfig);
export const useAgentOssQryLiveStream = makeRequestHook<
  AgentOssQryLiveStreamRequest,
  AgentOssQryLiveStreamRequestConfig,
  ReturnType<typeof agentOssQryLiveStream>
>(agentOssQryLiveStream);

export interface AgentOssQryLiveSessionInfoRequest {
  /**
   * 经纪人 uid，可选；代理 / 子代理视角传入时按该经纪人范围校验主播归属。
   */
  broker_uid?: number;
  /**
   * 批量查询项列表，每项使用 uid + roomId 锁定一场直播。
   */
  session_list: {
    /**
     * 主播 uid，必须属于当前登录代理 / 经纪人的可见范围。
     */
    uid: number;
    /**
     * 房间 id，用于锁定本次直播场次。
     */
    room_id: string;
  }[];
}

export interface AgentOssQryLiveSessionInfoItem {
  /**
   * 主播 uid。
   */
  uid: number;
  /**
   * 请求锁定的房间 id。
   */
  room_id: string;
  /**
   * 主播当前是否仍在该 roomId 直播。
   */
  living: boolean;
  /**
   * 本次开播时间，Unix 秒级时间戳；未直播或房间不匹配时为 0。
   */
  live_start_time: number;
  /**
   * 本场金豆数，等于当前直播间魅力金豆累计值减开播魅力金豆基线，异常或基线兜底时返回 0。
   */
  session_beans: number;
}

export type AgentOssQryLiveSessionInfoResponse = AgentOssQryLiveSessionInfoItem[];

type AgentOssQryLiveSessionInfoRequestConfig = Readonly<
  RequestConfig<
    'http://yapi.ppx520.com/mock/3957',
    '/bigo_act_livedata_api/agentOss/qryLiveSessionInfo',
    'data',
    string,
    string,
    false
  >
>;

const agentOssQryLiveSessionInfoRequestConfig = {
  mockUrl,
  path: '/bigo_act_livedata_api/agentOss/qryLiveSessionInfo',
  isYapi: true,
  isC: false,
  method: Method.POST,
  requestHeaders: {},
  requestBodyType: RequestBodyType.json,
  responseBodyType: ResponseBodyType.json,
  dataKey,
  paramNames: [],
  queryNames: [],
  requestDataOptional: false,
  requestDataJsonSchema: {},
  responseDataJsonSchema: {},
  requestFunctionName: 'agentOssQryLiveSessionInfo',
};

export const agentOssQryLiveSessionInfo = makeRequest<
  AgentOssQryLiveSessionInfoRequest,
  AgentOssQryLiveSessionInfoResponse
>(agentOssQryLiveSessionInfoRequestConfig);
export const useAgentOssQryLiveSessionInfo = makeRequestHook<
  AgentOssQryLiveSessionInfoRequest,
  AgentOssQryLiveSessionInfoRequestConfig,
  ReturnType<typeof agentOssQryLiveSessionInfo>
>(agentOssQryLiveSessionInfo);
